import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import Reveal from "../motion/Reveal";

// Small shared pieces. Kept in one file because each is a handful of lines and
// splitting them across seven modules would cost more than it explains.

// ── Brand mark ─────────────────────────────────────────────────────────────
// The real mark: a white node lattice on transparency, so it sits directly on
// the surface colour with nothing behind it.
//
// It carries a lot of internal detail, which is why it is set at 26px rather
// than the 18px placeholder in the design file. Below roughly 24px the lattice
// collapses into a speckle and reads as noise. The favicons solve the same
// problem with a wider inset (scripts/build-icons.js).
export function BrandMark({ size = 26, className = "" }) {
  return (
    <img
      src="/brand/mark.png"
      width={size}
      height={size}
      alt=""
      aria-hidden="true"
      draggable="false"
      className={`select-none ${className}`}
    />
  );
}

export function Wordmark({ to, className = "" }) {
  const inner = (
    <>
      <BrandMark />
      <span className="font-heading font-semibold text-[16px] tracking-[-0.2px] text-fg">
        Astrid Ecosystems
      </span>
    </>
  );
  const cls = `group inline-flex items-center gap-2.5 ${className}`;
  return to ? (
    <Link to={to} className={cls}>
      {inner}
    </Link>
  ) : (
    <span className={cls}>{inner}</span>
  );
}

// ── Eyebrow ────────────────────────────────────────────────────────────────
// Accent square plus a wide-tracked mono label. Opens every page.
export function Eyebrow({ children, pulse = false }) {
  return (
    // items-start rather than items-center: these labels wrap to two lines on
    // narrow viewports, and a centred dot then floats between them. The offset
    // keeps it aligned to the first line at any width.
    <div className="flex items-start gap-3">
      <span
        className={`mt-[5px] h-1.5 w-1.5 shrink-0 bg-accent ${pulse ? "animate-pulse-dot" : ""}`}
        aria-hidden="true"
      />
      <span className="t-eyebrow">{children}</span>
    </div>
  );
}

// ── Buttons ────────────────────────────────────────────────────────────────
const VARIANTS = {
  accent: "btn-accent",
  light: "btn-light",
  dark: "btn-dark",
};

export function Button({
  to,
  href,
  variant = "accent",
  size = "lg",
  arrow = true,
  icon: Icon,
  className = "",
  children,
  ...rest
}) {
  const cls = `${size === "sm" ? "btn-sm" : "btn-lg"} ${VARIANTS[variant]} ${className}`;
  const body = (
    <>
      {Icon && <Icon size={16} strokeWidth={2} aria-hidden="true" />}
      <span>{children}</span>
      {arrow && !Icon && <ArrowRight size={16} strokeWidth={2} className="btn-arrow" aria-hidden="true" />}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={cls} {...rest}>
        {body}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={cls} {...rest}>
        {body}
      </a>
    );
  }
  return (
    <button type="button" className={cls} {...rest}>
      {body}
    </button>
  );
}

// ── Chip ───────────────────────────────────────────────────────────────────
const CHIPS = {
  accent: "bg-chip-accent text-accent",
  neutral: "bg-chip-neutral text-fg-secondary",
  warn: "bg-chip-warn text-accent-2",
};

export function Chip({ tone = "neutral", children }) {
  return <span className={`chip ${CHIPS[tone]}`}>{children}</span>;
}

// ── Rail ───────────────────────────────────────────────────────────────────
// Left column of every block: accent index, title, optional caption note.
// On narrow viewports it stops being a rail and simply sits above the content.
export function Rail({ index, title, note, large = false, className = "" }) {
  return (
    <div className={`flex flex-col gap-3.5 ${className}`}>
      {index && <span className="font-mono text-[12px] tracking-[1.2px] text-accent">{index}</span>}
      <h2 className={large ? "t-rail-lg" : "t-rail"}>{title}</h2>
      {note && <p className="font-caption text-[13px] leading-[1.6] text-fg-muted">{note}</p>}
    </div>
  );
}

// ── Section ────────────────────────────────────────────────────────────────
// Standard block: hairline on top, gutter, rail beside content. The rail and
// body stack on anything narrower than a laptop.
export function Section({ index, title, note, children, className = "", railClassName = "" }) {
  return (
    <section className={`section section-t ${className}`}>
      <div className="shell flex flex-col gap-8 lg:flex-row lg:gap-20">
        {title && (
          <Reveal className={`lg:w-rail lg:shrink-0 ${railClassName}`}>
            <Rail index={index} title={title} note={note} />
          </Reveal>
        )}
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </section>
  );
}
